import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../api';
import { EntriesContext } from '../context/EntriesContext';
import { PeopleContext } from '../context/PeopleContext';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import useCategories from '../hooks/useCategories';
import useUsers from '../hooks/useUsers';
import usePersistedFilters from '../hooks/usePersistedFilters';
import EntryFilterBar from '../components/EntryFilterBar';
import { projectStatusMeta } from '../utils/projectStatus';
import TaskLabelField from '../components/TaskLabelField';
import CategoryCell from '../components/CategoryCell';
import WorkNoteCell from '../components/WorkNoteCell';
import BrutoCell from '../components/BrutoCell';
import {
  getCategoryName,
  isBrutoRuleActive,
  normalizeEntryDate,
  previewBruto,
  resolveEntryDate,
  formatHours,
} from '../utils/bruto';

// Helper to format ISO or DD/MM/YYYY date to DD/MM/YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  // If already DD/MM/YYYY, return as is
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
  // Try to parse as ISO
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('en-GB');
};

const getEntryDate = (entry) => entry.date || entry.dateCreated || '';

const croatianCollator = new Intl.Collator('hr', {
  numeric: true,
  sensitivity: 'base',
});

const getDateSortValue = (value) => {
  if (!value) return Number.NEGATIVE_INFINITY;
  const dmy = String(value).match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (dmy) return Date.UTC(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
  const iso = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  return Number.NEGATIVE_INFINITY;
};

const SortIndicator = ({ active, direction }) => (
  <span
    aria-hidden="true"
    className={`ml-1 ${active ? 'text-accent font-medium' : 'text-faint'}`}
  >
    {active ? (direction === 'asc' ? '↑' : '↓') : '↕'}
  </span>
);

export default function TimeEntry({ loggedInUser, role, isAdmin }) {
  // Changing a group's Requested by / department is limited to admins and designers (the server enforces it too).
  const canEditGroupMeta = Boolean(isAdmin) || role === 'admin' || role === 'designer';
  // Context hooks at top level
  const { entries, setEntries, fetchEntries } = useContext(EntriesContext);
  const { people } = useContext(PeopleContext);
  const { categories, activeCategories, byName: categoryByName, error: categoriesError } = useCategories();
  const userOptions = useUsers(loggedInUser);

  // State for editing category (assume object with id and name)
  const [editCategory, setEditCategory] = useState(null);
  // State for editing date and hours for child entry
  const [editDate, setEditDate] = useState('');
  const [editHours, setEditHours] = useState('');
  // State for modal edit example
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  // State for inline add form per group
  const [openAddFormKey, setOpenAddFormKey] = useState(null);
  const [addFormCategory, setAddFormCategory] = useState('');
  const [addFormHours, setAddFormHours] = useState('');
  const [addFormTaskLabel, setAddFormTaskLabel] = useState('');
  const [addFormError, setAddFormError] = useState('');
  // Edit state for Bruto / task label of a child entry
  const [editTaskLabel, setEditTaskLabel] = useState('');
  const [editBruto, setEditBruto] = useState('');
  const [editBrutoOverridden, setEditBrutoOverridden] = useState(false);
  const [editError, setEditError] = useState('');

  const [showAddProject, setShowAddProject] = useState(false);
  const [newProject, setNewProject] = useState('');
  const [newClientId, setNewClientId] = useState('');
  const [clients, setClients] = useState([]);
  const [newRequestedBy, setNewRequestedBy] = useState('');
  const [newDepartment, setNewDepartment] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [newUser, setNewUser] = useState(loggedInUser || '');

  const [editingId, setEditingId] = useState(null);
  // Fetch clients from backend
  useEffect(() => {
    async function fetchClients() {
      try {
        const res = await axios.get(`${API_BASE_URL}/clients`);
        setClients(res.data);
      } catch (err) {
        toast.error('Failed to load clients');
      }
    }
    fetchClients();
  }, []);
  const [editRequestedBy, setEditRequestedBy] = useState('');
  const [editDepartment, setEditDepartment] = useState('');
  const [editDateCreated, setEditDateCreated] = useState('');
  const [editParentOriginal, setEditParentOriginal] = useState(null); // for cancel
  const [editGroupError, setEditGroupError] = useState({ field: '', message: '' }); // inline, field-level

  const [activeTimer, setActiveTimer] = useState(null); // ID projekta
  const [timerStart, setTimerStart] = useState(null);   // Start time
  const [elapsed, setElapsed] = useState(0);
  const [timerHours, setTimerHours] = useState(0);      // Hours added by timer
  const [entryElapsed, setEntryElapsed] = useState({}); // { [id]: ms }

  // Filters and sorting are persisted per user (API, with localStorage fallback).
  const { prefs, setPref, resetFilters, activeFilterKeys } = usePersistedFilters(loggedInUser);
  const {
    filterCategory, filterClient, filterRequestedBy, filterUser, filterDepartment, filterYear, filterMonth,
    sortBy: sortColumn, sortDir: sortDirection,
  } = prefs;

  const handleSort = (column) => {
    if (sortColumn === column) {
      setPref('sortDir', sortDirection === 'asc' ? 'desc' : 'asc');
      return;
    }
    setPref('sortBy', column);
    setPref('sortDir', 'asc');
  };

  // Function to handle editing a child entry (not parent row)
  function handleEdit(entry) {
    setEditingId(entry.id);
    // If entry.category is an object, use as is; if string, wrap as { id, name }
    if (entry.category && typeof entry.category === 'object') {
      setEditCategory(entry.category);
    } else if (entry.category) {
      setEditCategory({ id: entry.id, name: entry.category });
    } else {
      setEditCategory(null);
    }
    // Entry date is `date`, falling back to dateCreated (this is what Bruto is based on).
    setEditDate(entry.date || entry.dateCreated || '');
    setEditHours(entry.hours || '');
    setEditTaskLabel(entry.task_label || '');
    setEditBruto(entry.hours_bruto ?? entry.hours ?? '');
    setEditBrutoOverridden(Boolean(entry.bruto_overridden));
    setEditError('');
  }

  // Does this user already have this category on this project (other than `excludeId`)?
  const categoryExistsForUser = (entry, categoryName, excludeId) => entries.some(e =>
    e.id !== excludeId &&
    e.project_id === entry.project_id &&
    e.user === loggedInUser &&
    getCategoryName(e.category) === categoryName
  );

  // When editing: an empty label is only a problem if another entry of this category has an empty label too
  // (mirrors the server rule, so an older unlabelled entry is not forced to be renamed retroactively).
  const emptyLabelCollides = (entry, categoryName, excludeId) => entries.some(e =>
    e.id !== excludeId &&
    e.project_id === entry.project_id &&
    e.user === loggedInUser &&
    getCategoryName(e.category) === categoryName &&
    !(e.task_label || '').trim()
  );

  // Live Bruto preview for the row being edited (server stays the source of truth).
  const getEditBrutoValue = () => {
    if (editBrutoOverridden) return editBruto;
    const mult = categoryByName[editCategory?.name]?.bruto_multiplier;
    return previewBruto(editHours, mult, normalizeEntryDate(editDate));
  };

  // A manual Bruto is discarded when Neto/date/category change — warn before saving.
  const confirmOverrideLoss = (entry, next = {}) => {
    if (!entry.bruto_overridden) return true;
    const netoChanged = next.hours !== undefined && Number(next.hours) !== Number(entry.hours);
    const dateChanged = next.date !== undefined && normalizeEntryDate(next.date) !== resolveEntryDate(entry);
    const catChanged = next.category !== undefined && next.category !== getCategoryName(entry.category);
    if (!netoChanged && !dateChanged && !catChanged) return true;
    return window.confirm('The manual Bruto will be recalculated. Continue?');
  };

  // Finish / reopen a project. The server also finishes projects automatically after 14 days without hours.
  const handleSetProjectStatus = async (projectId, projectName, status) => {
    if (status === 'finished' && !window.confirm(`Finish project "${projectName}"?`)) return;
    try {
      await axios.put(`${API_BASE_URL}/api/projects/${projectId}/status`, { status });
      await fetchEntries();
      toast.success(status === 'finished' ? 'Project finished' : 'Project reopened');
    } catch {
      toast.error('Failed to update project status');
    }
  };

  const handleSaveNote = async (entryId, text) => {
    await axios.put(`${API_BASE_URL}/api/entries/${entryId}/note`, { user: loggedInUser, work_note: text });
    await fetchEntries();
    toast.success('Description saved');
  };

  // Function to handle editing a parent project row.
  // Project name and client are read-only here (renaming lives on the Projects page).
  function handleEditParent(parent) {
    setEditingId(parent.id);
    setEditRequestedBy(parent.requestedBy || '');
    setEditDepartment(parent.department || '');
    setEditDateCreated(parent.dateCreated || parent.date);
    setEditParentOriginal(parent);
    setEditGroupError({ field: '', message: '' });
  }

  const resetParentEdit = () => {
    setEditingId(null);
    setEditRequestedBy('');
    setEditDepartment('');
    setEditDateCreated('');
    setEditParentOriginal(null);
    setEditGroupError({ field: '', message: '' });
  };

  const handleCancelEditParent = () => resetParentEdit();

  // Picking a person fills in their department; the field stays editable for a deliberate mismatch.
  const handleEditRequestedByChange = (name) => {
    setEditRequestedBy(name);
    setEditGroupError({ field: '', message: '' });
    const person = people.find(p => p.name === name);
    if (person && person.department) setEditDepartment(person.department);
  };

  const handleSaveEditParent = async (id) => {
    const originalRequested = editParentOriginal?.requestedBy || '';
    const originalDept = editParentOriginal?.department || '';
    const requestedChanged = editRequestedBy !== originalRequested;

    if (requestedChanged) {
      // The change applies to the whole group, including rows owned by other users — say so before saving.
      const groupEntries = entries.filter(e =>
        e.project_id === editParentOriginal?.project_id
        && (e.requestedBy || '') === originalRequested
        && (e.department || '') === originalDept);
      const owners = [...new Set(groupEntries.map(e => e.user).filter(Boolean))].sort();
      const message = `Change "Requested by" from "${originalRequested || '—'}" to "${editRequestedBy || '—'}"?\n\n`
        + `This moves ${groupEntries.length} ${groupEntries.length === 1 ? 'entry' : 'entries'}`
        + `${owners.length ? ` owned by ${owners.join(', ')}` : ''}.\n`
        + 'Rows owned by other users are affected too; their hours and notes stay untouched.';
      if (!window.confirm(message)) return;
    }

    try {
      const res = await axios.put(`${API_BASE_URL}/api/entries/${id}/group-meta`,
        {
          project_id: editParentOriginal?.project_id,
          requestedBy: editRequestedBy,
          department: editDepartment,
          dateCreated: editDateCreated,
          originalRequestedBy: originalRequested,
          originalDepartment: originalDept
        }
      );
      resetParentEdit();
      fetchEntries();
      const { updatedGroupEntries, owners, merged } = res.data || {};
      if (requestedChanged && Number.isFinite(updatedGroupEntries)) {
        const who = Array.isArray(owners) && owners.length ? ` (${owners.join(', ')})` : '';
        const count = `${updatedGroupEntries} ${updatedGroupEntries === 1 ? 'entry' : 'entries'}`;
        toast.success(merged
          ? `Requested by updated on ${count}${who}; merged into an existing group.`
          : `Requested by updated on ${count}${who}.`);
      } else {
        toast.success(merged ? 'Project updated; merged into an existing group.' : 'Project updated');
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.field === 'requestedBy' || data?.field === 'department') {
        setEditGroupError({ field: data.field, message: data.error });
      } else {
        toast.error(data?.error || 'Failed to update project');
      }
    }
  };

  const handleSaveEdit = async (id, entry) => {
    const hours = parseFloat(editHours) || 0;
    const categoryNameValue = editCategory ? editCategory.name : '';
    if (emptyLabelCollides(entry, categoryNameValue, id) && !editTaskLabel.trim()) {
      setEditError('Task name is required because this category already exists on the project.');
      return;
    }
    if (!confirmOverrideLoss(entry, { hours, date: editDate, category: categoryNameValue })) return;
    try {
      await axios.put(`${API_BASE_URL}/api/entries/${id}`, {
        ...entry,
        hours,
        category: categoryNameValue,
        category_id: categoryByName[categoryNameValue]?.id ?? null,
        date: editDate,
        task_label: editTaskLabel.trim(),
        hours_bruto: Number(getEditBrutoValue()),
        bruto_overridden: editBrutoOverridden ? 1 : 0,
      });
      setEditingId(null);
      setEditError('');
      fetchEntries();
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to update entry';
      if (err.response?.data?.field) setEditError(message); else toast.error(message);
    }
  };

  const handleAddProject = async () => {
    if (!newProject || !newClientId || !newRequestedBy || !newDepartment) {
      toast.error('All fields are required');
      return;
    }

    // Check for duplicate project+client+user (no category check here)
    const duplicate = entries.some(e =>
      e.project === newProject &&
      e.client_id === newClientId &&
      e.user === loggedInUser
    );
    if (duplicate) {
      toast.error('You already have this project and client.');
      return;
    }

    const department = newDepartment;
    const date = new Date();
    const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1)
      .toString().padStart(2, '0')}/${date.getFullYear()}`;

    try {
      // Always create a child entry for the new project
      await axios.post(`${API_BASE_URL}/api/entries`, {
        project: newProject,
        client_id: newClientId,
        requestedBy: newRequestedBy,
        department,
        hours: 0,
        date: formattedDate,
        dateCreated: formattedDate,
        dateModified: formattedDate,
        user: loggedInUser
      });
      // Wait for fetchEntries to complete before clearing fields
      await fetchEntries();
      setNewProject('');
      setNewClientId('');
      setNewRequestedBy('');
      setShowAddProject(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add entry');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) {
      toast('Delete cancelled');
      return;
    }
    try {
      await axios.delete(`${API_BASE_URL}/api/entries/${id}`);
      fetchEntries();
      toast.success('Entry deleted');
    } catch {
      toast.error('Failed to delete entry');
    }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  useEffect(() => {
    let interval;
    if (activeTimer && timerStart) {
      interval = setInterval(() => {
        const msElapsed = (Date.now() - timerStart) + (entryElapsed[activeTimer] || 0);
        setElapsed(msElapsed);

        // Calculate how many 30-min units have passed
        const units = Math.floor(msElapsed / (1000 * 60 * 30));
        setTimerHours(units * 0.5);
      }, 1000);
    } else {
      setElapsed(0);
      setTimerHours(0);
    }
    return () => clearInterval(interval);
  }, [activeTimer, timerStart, entryElapsed]);

  // Helper to format ms to hh:mm:ss
  const formatElapsed = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return [
      hours.toString().padStart(2, '0'),
      minutes.toString().padStart(2, '0'),
      seconds.toString().padStart(2, '0')
    ].join(':');
  };

  const handleExportExcel = () => {
    const rows = filteredEntries.map(e => ({
      Project: e.project,
      Client: e.client || '',
      'Requested By': e.requestedBy || '',
      Department: e.department || '',
      Category: getCategoryName(e.category),
      'Task name': e.task_label || '',
      'Date Created': formatDate(e.dateCreated || e.date),
      'Date Modified': formatDate(e.dateModified || e.date),
      User: e.user || '',
      Neto: Number(e.hours) || 0,
      Bruto: e.hours_bruto === null || e.hours_bruto === undefined ? Number(e.hours) || 0 : Number(e.hours_bruto),
      'Work description': (e.work_note || '').slice(0, 2000),
      'Description updated by': e.note_updated_by || '',
      'Description updated at': e.note_updated_at ? new Date(e.note_updated_at).toLocaleDateString('en-GB') : '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Time Entries');
    XLSX.writeFile(wb, 'time_entries.xlsx');
  };

  const startTimer = (projectId) => {
    if (activeTimer) {
      toast.error('Another timer is running. Stop it first.');
      return;
    }
    setActiveTimer(projectId);
    setTimerStart(Date.now() - (entryElapsed[projectId] || 0));
    setTimerHours(0);
  };

  // Only add time spent during this session
  const stopTimer = async (projectId) => {
    if (!activeTimer || activeTimer !== projectId) return;

    const entry = entries.find((e) => e.id === projectId);
    const prevHours = parseFloat(entry?.hours) || 0;
    const msElapsed = Date.now() - timerStart;
    const totalElapsed = msElapsed;

    // Only count new time spent during this session
    const newUnits = Math.floor(totalElapsed / (1000 * 60 * 30));
    const newHours = newUnits * 0.5;

    // Save the remainder ms for next session
    setEntryElapsed((prev) => ({
      ...prev,
      [projectId]: totalElapsed % (1000 * 60 * 30)
    }));

    if (entry && newHours > 0) {
      // Set date to today (DD/MM/YYYY) before saving
      const now = new Date();
      const todayStr = `${now.getDate().toString().padStart(2, '0')}/${(now.getMonth() + 1).toString().padStart(2, '0')}/${now.getFullYear()}`;
      await axios.put(`${API_BASE_URL}/api/entries/${entry.id}`, {
        ...entry,
        hours: prevHours + newHours,
        date: todayStr
      });
    }
    setActiveTimer(null);
    setTimerStart(null);
    setTimerHours(0);
    fetchEntries();
  };

  const filteredEntries = entries.filter((entry) => {
    const entryCategory = getCategoryName(entry.category);
    const matchCategory = filterCategory ? entryCategory === filterCategory : true;
    const clientName = (entry.client || '').toLowerCase();
    const matchClient = filterClient ? clientName === filterClient.toLowerCase() : true;
    const matchDepartment = filterDepartment ? entry.department === filterDepartment : true;
    const matchRequestedBy = filterRequestedBy ? entry.requestedBy === filterRequestedBy : true;
    const matchUser = filterUser ? entry.user === filterUser : true;
    const query = searchQuery.toLowerCase();
    const matchSearch =
      (entry.project || '').toLowerCase().includes(query) ||
      clientName.includes(query);

    // Filter by year and month (expects entry.date in DD/MM/YYYY format)
    const entryDate = getEntryDate(entry);
    // Dates in the database are a mix of DD/MM/YYYY and ISO — normalise before comparing.
    const iso = normalizeEntryDate(String(entryDate));
    const eYear = iso ? iso.slice(0, 4) : undefined;
    const eMonth = iso ? iso.slice(5, 7) : undefined;
    const matchYear = filterYear ? eYear === filterYear : true;
    const matchMonth = filterMonth ? eMonth === filterMonth : true;

    return matchCategory && matchClient && matchDepartment && matchRequestedBy && matchUser && matchSearch && matchYear && matchMonth;
  });

  const netoOf = (entry) => parseFloat(entry.hours) || 0;
  // Legacy rows without hours_bruto fall back to Neto.
  const brutoOf = (entry) => (entry.hours_bruto === null || entry.hours_bruto === undefined
    ? netoOf(entry) : Number(entry.hours_bruto) || 0);
  const totalHours = filteredEntries.reduce((sum, entry) => sum + netoOf(entry), 0);
  const totalBruto = filteredEntries.reduce((sum, entry) => sum + brutoOf(entry), 0);
  const summaryCategoryTotals = filteredEntries.reduce((totals, entry) => {
    const categoryName = getCategoryName(entry.category);
    if (!categoryName) return totals;
    const t = totals[categoryName] || { neto: 0, bruto: 0 };
    t.neto += netoOf(entry);
    t.bruto += brutoOf(entry);
    totals[categoryName] = t;
    return totals;
  }, {});

  // Order comes from the categories table (sort_order); unknown/legacy names go last, alphabetically.
  const knownOrder = categories.map(c => c.name);
  const summaryCategories = [
    ...knownOrder.filter(category => Object.prototype.hasOwnProperty.call(summaryCategoryTotals, category)),
    ...Object.keys(summaryCategoryTotals)
      .filter(category => !knownOrder.includes(category))
      .sort((a, b) => a.localeCompare(b)),
  ];

  // --- Grouping by Project+Client+RequestedBy+Department and sorting by most recent child ---
  // Helper to get date string in DD/MM/YYYY
  const getDateStr = (date) => {
    if (!date) return '';
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(date)) return date;
    const d = new Date(date);
    if (isNaN(d)) return date;
    return d.toLocaleDateString('en-GB');
  };

  // Get today's and yesterday's date strings
  const today = new Date();
  const todayStr = today.toLocaleDateString('en-GB');
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const yesterdayStr = yesterday.toLocaleDateString('en-GB');
  const currentMonth = (today.getMonth() + 1).toString().padStart(2, '0');
  const currentYear = today.getFullYear().toString();

  // Group entries by Project+ClientId+RequestedBy+Department (use client_id for grouping)
  const groupMap = {};
  filteredEntries.forEach(entry => {
    // Find client name by id for display
    const clientObj = clients.find(c => c.id === entry.client_id);
    const clientName = clientObj ? clientObj.name : (entry.client || '');
    const key = `${entry.project}|||${entry.client_id}|||${entry.requestedBy}|||${entry.department}`;
    if (!groupMap[key]) groupMap[key] = { entries: [], clientName };
    groupMap[key].entries.push(entry);
  });

  // For each group, find the most recent dateModified
  const groupList = Object.entries(groupMap).map(([key, groupObj]) => {
    const { entries, clientName } = groupObj;
    const mostRecent = entries.reduce((latest, e) => {
      const d = getDateStr(e.dateModified || e.date);
      if (!latest) return { date: d, entry: e };
      // Compare dates
      const [ld, lm, ly] = latest.date.split('/');
      const [cd, cm, cy] = d.split('/');
      const lDate = new Date(`${ly}-${lm}-${ld}`);
      const cDate = new Date(`${cy}-${cm}-${cd}`);
      return cDate > lDate ? { date: d, entry: e } : latest;
    }, null);
    return {
      key,
      entries,
      clientName,
      mostRecentDate: mostRecent ? mostRecent.date : '',
      mostRecentEntry: mostRecent ? mostRecent.entry : null,
      project: entries[0]?.project || '',
      requestedBy: entries[0]?.requestedBy || '',
      department: entries[0]?.department || '',
      category: entries.map(e => getCategoryName(e.category)).filter(Boolean)
        .sort((a, b) => croatianCollator.compare(a, b))[0] || '',
      dateCreated: entries.reduce((earliest, e) => {
        const value = e.dateCreated || e.date || '';
        return !earliest || getDateSortValue(value) < getDateSortValue(earliest) ? value : earliest;
      }, ''),
      totalHours: entries.reduce((sum, e) => sum + (Number(e.hours) || 0), 0),
      totalBruto: entries.reduce((sum, e) => sum + brutoOf(e), 0),
      user: entries.map(e => e.user).filter(Boolean)
        .sort((a, b) => croatianCollator.compare(a, b))[0] || ''
    };
  });

  const compareGroups = (a, b) => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    let result;
    if (sortColumn === 'dateCreated') {
      result = getDateSortValue(a.dateCreated) - getDateSortValue(b.dateCreated);
    } else if (sortColumn === 'dateModified') {
      result = getDateSortValue(a.mostRecentDate) - getDateSortValue(b.mostRecentDate);
    } else if (sortColumn === 'hours') {
      result = a.totalHours - b.totalHours;
    } else if (sortColumn === 'bruto') {
      result = a.totalBruto - b.totalBruto;
    } else {
      const values = {
        project: [a.project, b.project], client: [a.clientName, b.clientName],
        requestedBy: [a.requestedBy, b.requestedBy], department: [a.department, b.department],
        category: [a.category, b.category], user: [a.user, b.user],
      }[sortColumn] || ['', ''];
      result = croatianCollator.compare(values[0], values[1]);
    }
    if (result !== 0) return result * direction;
    return (getDateSortValue(b.mostRecentDate) - getDateSortValue(a.mostRecentDate));
  };

  // Assign section (Today, Yesterday, etc.) based on most recent date in group
  function getSection(dateStr) {
    if (!dateStr) return 'Other';
    if (dateStr === todayStr) return 'Today';
    if (dateStr === yesterdayStr) return 'Yesterday';
    const [d, m, y] = dateStr.split('/');
    if (m !== currentMonth || y !== currentYear) return 'Last month';
    // Use weekday, DD/MM/YYYY
    const dateObj = new Date(`${y}-${m}-${d}`);
    const weekday = dateObj.toLocaleDateString('en-GB', { weekday: 'long' });
    return `${weekday}, ${dateStr}`;
  }

  // Group groups by section
  const sectionMap = {};
  groupList.forEach(group => {
    const section = getSection(group.mostRecentDate);
    if (!sectionMap[section]) sectionMap[section] = [];
    sectionMap[section].push(group);
  });
  Object.values(sectionMap).forEach(groups => groups.sort(compareGroups));

  // Sort section keys: Today, Yesterday, then by date descending, then Last month last
  const sectionOrder = Object.keys(sectionMap).sort((a, b) => {
    if (a === 'Today') return -1;
    if (b === 'Today') return 1;
    if (a === 'Yesterday') return -1;
    if (b === 'Yesterday') return 1;
    if (a === 'Last month') return 1;
    if (b === 'Last month') return -1;
    // For weekday, DD/MM/YYYY, sort by date descending
    const dateA = a.match(/\d{2}\/\d{2}\/\d{4}/);
    const dateB = b.match(/\d{2}\/\d{2}\/\d{4}/);
    if (dateA && dateB) {
      const [dA, mA, yA] = dateA[0].split('/');
      const [dB, mB, yB] = dateB[0].split('/');
      const da = new Date(`${yA}-${mA}-${dA}`);
      const db = new Date(`${yB}-${mB}-${dB}`);
      return db - da;
    }
    return 0;
  });

  const sortableHeader = (label, column, right = false) => {
    const active = sortColumn === column;
    return (
      <th
        aria-sort={active ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
        className={`${thBase} ${right ? 'text-right' : ''}`}
      >
        <button type="button" className="font-medium whitespace-nowrap" onClick={() => handleSort(column)}>
          {label}<SortIndicator active={active} direction={sortDirection} />
        </button>
      </th>
    );
  };

  const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const fmtHrs = (n) => (Number(n) || 0).toFixed(1);
  const periodLabel = (() => {
    const monthIdx = filterMonth ? parseInt(filterMonth, 10) - 1 : -1;
    const monthName = MONTH_NAMES[monthIdx] || '';
    return [monthName, filterYear].filter(Boolean).join(' ') || 'All entries';
  })();
  // Departments come from /api/people plus whatever already sits on entries.
  const departmentOptions = [...new Set([...people.map(p => p.department), ...entries.map(e => e.department)].filter(Boolean))].sort();
  const missingNoteCount = filteredEntries.filter((e) => !e.work_note).length;
  const iconBtnBase = 'inline-flex items-center justify-center w-11 h-11 rounded-tc-sm';
  const svgIcon = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
  const fieldClass = 'w-full text-[13px] text-ink bg-surface border border-line-strong rounded-tc-sm px-3 py-2.5 min-h-[44px]';
  const microLabel = 'block text-[10px] font-semibold uppercase tracking-[0.1em] text-muted mb-1';
  const statusClass = {
    in_progress: 'bg-state-progress-bg text-state-progress-fg',
    in_review: 'bg-state-review-bg text-state-review-fg',
    finished: 'bg-state-done-bg text-state-done-fg',
  };
  const stepBtn = 'w-9 h-11 text-base text-ink bg-surface-alt disabled:opacity-40 disabled:cursor-not-allowed';
  const thBase = 'text-left font-medium text-muted px-4 py-3 border-b border-line whitespace-nowrap';
  const tdBase = 'px-4 py-2.5 align-top';

  return (
    <div>
      <div className="border-b border-line py-8 mb-6">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-accent mb-2">{periodLabel}</div>
            <h1 className="font-display text-[46px] leading-none tracking-tight">Time Entry</h1>
          </div>
          <div className="flex flex-wrap gap-10">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted mb-1">Neto</div>
              <div className="text-[32px] font-semibold tracking-tight tabular">{fmtHrs(totalHours)}</div>
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted mb-1">Bruto</div>
              <div className="text-[32px] font-semibold tracking-tight tabular text-accent">{fmtHrs(totalBruto)}</div>
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted mb-1">No description</div>
              <div className="text-[32px] font-semibold tracking-tight tabular">{String(missingNoteCount).padStart(2, '0')}</div>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap justify-end gap-2 mt-6">
          <button
            onClick={() => setShowAddProject(v => !v)}
            aria-expanded={showAddProject}
            className="bg-accent text-white text-[13px] font-semibold rounded-tc-sm px-4 min-h-[44px]"
          >
            {showAddProject ? 'Close' : 'New project'}
          </button>
          <button
            onClick={handleExportExcel}
            className="inline-flex items-center gap-2 bg-surface text-ink text-[13px] border border-line rounded-tc-sm px-3.5 min-h-[44px]"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v12M7 10l5 5 5-5M4 21h16" /></svg>
            Export XLSX
          </button>
        </div>
      </div>

      {/* New project form (collapsed by default) */}
      {showAddProject && (
      <div className="flex flex-wrap items-end gap-3 bg-surface border border-line rounded-tc-lg p-6 mb-5">
        <input
          type="text"
          value={newProject}
          onChange={(e) => setNewProject(e.target.value)}
          placeholder="Project Name"
          aria-label="Project Name"
          className={`${fieldClass} flex-1 min-w-[10rem]`}
        />
        <select
          value={newClientId}
          aria-label="Client"
          onChange={e => setNewClientId(e.target.value)}
          className={`${fieldClass} flex-1 min-w-[10rem]`}
        >
          <option value="">Select Client</option>
          {clients.map(client => (
            <option key={client.id} value={client.id}>{client.name}</option>
          ))}
        </select>
        <select
          value={newRequestedBy}
          aria-label="Requested By"
          onChange={(e) => {
            const selected = people.find(p => p.name === e.target.value);
            setNewRequestedBy(e.target.value);
            setNewDepartment(selected ? selected.department : '');
          }}
          className={`${fieldClass} flex-1 min-w-[10rem]`}
        >
          <option value="">Requested By</option>
          {people.map((person) => (
            <option key={person.id} value={person.name}>{person.name}</option>
          ))}
        </select>
        <select
          value={newDepartment}
          aria-label="Department"
          onChange={(e) => setNewDepartment(e.target.value)}
          className={`${fieldClass} flex-1 min-w-[10rem]`}
        >
          <option value="">Department</option>
          <option value="Live">Live</option>
          <option value="Retail">Retail</option>
          <option value="Envy">Envy</option>
          <option value="MPG Live">MPG Live</option>
        </select>
        {/* Category selection removed from project creation. Category is chosen only when adding a child entry. */}
        <input
          type="text"
          value={loggedInUser}
          readOnly
          aria-label="User"
          className={`${fieldClass} flex-1 min-w-[10rem] bg-surface-alt text-muted`}
        />
        <button
          onClick={handleAddProject}
          className="bg-accent text-white font-semibold rounded-tc-sm px-5 min-h-[44px]"
        >
          Add
        </button>
      </div>
      )}

      {/* Summary Cards inline */}
      <div className="flex flex-wrap justify-between bg-surface border border-line text-ink p-3 rounded-tc-lg mb-4 text-sm tabular">
        <span className="text-faint font-medium self-center mr-4">Filtered totals:</span>
        <p className="self-center">Total Neto: <strong>{totalHours.toFixed(1)} h</strong></p>
        <p className="self-center">Total Bruto: <strong>{totalBruto.toFixed(1)} h</strong></p>
        {summaryCategories.map(category => (
          <p key={category} className="self-center">
            {category}: <strong>{summaryCategoryTotals[category].neto.toFixed(1)}</strong> / <strong>{summaryCategoryTotals[category].bruto.toFixed(1)} h</strong>
          </p>
        ))}
      </div>

      {/* Filters */}
      <EntryFilterBar
        values={prefs}
        onChange={setPref}
        onReset={() => { resetFilters(); setSearchQuery(''); }}
        activeKeys={activeFilterKeys}
        resultCount={filteredEntries.length}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        categories={categories}
        clients={clients}
        people={people}
        users={userOptions}
        departments={departmentOptions}
        years={[...new Set([...entries.map(e => (normalizeEntryDate(String(getEntryDate(e))) || '').slice(0, 4)), filterYear].filter(Boolean))].sort((a, b) => b - a)}
      />
      {categoriesError && (
        <p className="text-sm text-danger-ink mb-2">Categories could not be loaded. Refresh the page.</p>
      )}

      {/* Table */}
    <div className="bg-surface border border-line rounded-tc-lg overflow-x-auto">
      <table className="w-full border-collapse text-[12.5px] text-ink">
        <thead>
          <tr className="bg-surface-alt">
            {sortableHeader('Project', 'project')}
            {sortableHeader('Client', 'client')}
            {sortableHeader('Requested by', 'requestedBy')}
            {sortableHeader('Department', 'department')}
            {sortableHeader('Category', 'category')}
            {sortableHeader('Created', 'dateCreated')}
            {sortableHeader('Modified', 'dateModified')}
            {sortableHeader('Neto', 'hours', true)}
            {sortableHeader('Bruto', 'bruto', true)}
            <th className={thBase}>Time</th>
            <th className={thBase}>Actions</th>
            {sortableHeader('User', 'user')}
          </tr>
        </thead>
        <tbody>
          {/* Render by section, then by group */}
          {sectionOrder.map((section) => (
            <React.Fragment key={section}>
              <tr>
                <td colSpan={12} className="bg-surface text-ink text-[13px] font-semibold px-4 py-3 border-b border-line">{section}</td>
              </tr>
              {sectionMap[section].map(group => {
                // Sort group entries by dateCreated ascending for parent/children
                const sorted = [...group.entries].sort((a, b) => {
                  // Parent first if you have a parent flag, else by dateCreated
                  return getDateSortValue(a.dateCreated || a.date) -
                    getDateSortValue(b.dateCreated || b.date);
                });
                const parent = sorted[0];
                const children = sorted.slice(1);
                if (sortColumn === 'category') {
                  children.sort((a, b) => croatianCollator.compare(
                    getCategoryName(a.category), getCategoryName(b.category)
                  ) * (sortDirection === 'asc' ? 1 : -1));
                }
                const [project, clientId, requestedBy, department] = group.key.split('|||');
                const key = group.key;
                // Always use group.clientName for display
                const clientName = group.clientName;
                return (
                  <React.Fragment key={key}>
                    {/* Parent Row */}
                    <tr className="bg-surface-alt border-b border-line">
                      {/* Project Name */}
                      <td className={`${tdBase} flex items-center gap-2`}>
                        <span className="text-[13px] font-semibold text-ink">{project}</span>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center w-8 h-8 rounded-tc-sm border border-line bg-surface text-ink disabled:opacity-40 disabled:cursor-not-allowed"
                          aria-label="Add a category to this project"
                          title="Add a category to this project"
                          onClick={() => {
                            setOpenAddFormKey(key);
                            setAddFormCategory('');
                            setAddFormTaskLabel('');
                            setAddFormError('');
                          }}
                          disabled={editingId === parent.id}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
                        </button>
                        {editingId !== parent.id && (() => {
                          const meta = projectStatusMeta(parent.project_status);
                          return (
                            <span
                              className={`text-[11.5px] font-semibold rounded-tc-sm px-2 py-1 ${statusClass[meta.value] || statusClass.in_progress}`}
                              title={parent.project_status_auto ? 'Finished automatically: no hours logged for 14 days' : undefined}
                            >
                              {meta.label}{parent.project_status_auto ? ' (auto)' : ''}
                            </span>
                          );
                        })()}
                      </td>
                      {/* Client Name */}
                      <td className={tdBase}>
                        <span>{clientName}</span>
                      </td>
                      {/* Requested By */}
                      <td className={tdBase}>
                        {editingId === parent.id ? (
                          <>
                            <select
                              value={editRequestedBy}
                              aria-label="Requested by"
                              aria-invalid={editGroupError.field === 'requestedBy'}
                              onChange={e => handleEditRequestedByChange(e.target.value)}
                              className={`${fieldClass} !min-h-[38px] !py-1.5 ${editGroupError.field === 'requestedBy' ? '!border-[1.5px] !border-danger !bg-danger-soft' : ''}`}
                            >
                              <option value="">Requested By</option>
                              {/* A legacy name that is no longer in the people list stays selectable until changed. */}
                              {editRequestedBy && !people.some(p => p.name === editRequestedBy) && (
                                <option value={editRequestedBy}>{editRequestedBy}</option>
                              )}
                              {people.map((person) => (
                                <option key={person.id} value={person.name}>{person.name}</option>
                              ))}
                            </select>
                            {editGroupError.field === 'requestedBy' && (
                              <p className="text-[11px] text-danger-ink mt-1" role="alert">{editGroupError.message}</p>
                            )}
                          </>
                        ) : (
                          <span>{requestedBy}</span>
                        )}
                      </td>
                      {/* Department */}
                      <td className={tdBase}>
                        {editingId === parent.id ? (
                          <>
                            <select
                              value={editDepartment}
                              aria-label="Department"
                              aria-invalid={editGroupError.field === 'department'}
                              onChange={e => { setEditDepartment(e.target.value); setEditGroupError({ field: '', message: '' }); }}
                              className={`${fieldClass} !min-h-[38px] !py-1.5 ${editGroupError.field === 'department' ? '!border-[1.5px] !border-danger !bg-danger-soft' : ''}`}
                            >
                              <option value="">Department</option>
                              {[...new Set([...departmentOptions, editDepartment].filter(Boolean))].sort().map(d => (
                                <option key={d} value={d}>{d}</option>
                              ))}
                            </select>
                            {editGroupError.field === 'department' && (
                              <p className="text-[11px] text-danger-ink mt-1" role="alert">{editGroupError.message}</p>
                            )}
                          </>
                        ) : (
                          <span>{department}</span>
                        )}
                      </td>
                      <td className={tdBase}></td>
                      {/* Date Created (editable only in parent row) */}
                      <td className={tdBase}>
                        {editingId === parent.id ? (
                          <input
                            type="date"
                            value={(() => {
                              if (!editDateCreated) return '';
                              const [d, m, y] = editDateCreated.split('/');
                              return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
                            })()}
                            onChange={e => {
                              const [y, m, d] = e.target.value.split('-');
                              setEditDateCreated(`${d}/${m}/${y}`);
                            }}
                            className={`${fieldClass} !min-h-[38px] !py-1.5`}
                          />
                        ) : (
                          <span>{formatDate(parent.dateCreated || parent.date)}</span>
                        )}
                      </td>
                      {/* Date Modified — most recent across all entries in this group */}
                      <td className={tdBase}>
                        <span>{group.mostRecentDate || formatDate(parent.dateModified || parent.date)}</span>
                      </td>
                      <td className={`${tdBase} text-right font-semibold tabular`}>{fmtHrs(group.totalHours)}</td>
                      <td className={`${tdBase} text-right font-semibold tabular text-accent`}>{fmtHrs(group.totalBruto)}</td>
                      <td className={`${tdBase} text-faint`}>—</td>
                      {/* Actions */}
                      <td className={tdBase}>
                        {editingId === parent.id ? (
                          <>
                            <button
                              className="bg-accent text-white text-[12.5px] font-semibold rounded-tc-sm px-3 min-h-[36px] mr-2"
                              onClick={() => handleSaveEditParent(parent.id)}
                            >Save</button>
                            <button
                              className="bg-surface text-muted text-[12.5px] border border-line rounded-tc-sm px-3 min-h-[36px]"
                              onClick={handleCancelEditParent}
                            >Cancel</button>
                          </>
                        ) : (
                          <>
                            {canEditGroupMeta && (
                              <button
                                className="bg-surface text-ink text-[12.5px] border border-line rounded-tc-sm px-3 min-h-[36px]"
                                onClick={() => handleEditParent(parent)}
                              >Edit</button>
                            )}
                            {parent.project_status === 'finished' ? (
                              <button
                                className="ml-2 bg-surface text-muted text-[12.5px] border border-line rounded-tc-sm px-3 min-h-[36px]"
                                title="Set the project back to In progress"
                                onClick={() => handleSetProjectStatus(parent.project_id, project, 'in_progress')}
                              >Reopen</button>
                            ) : (
                              <button
                                className="ml-2 bg-state-done-bg text-state-done-fg text-[12.5px] font-semibold rounded-tc-sm px-3 min-h-[36px]"
                                title="Mark the project as Finished"
                                onClick={() => handleSetProjectStatus(parent.project_id, project, 'finished')}
                              >Finish project</button>
                            )}
                          </>
                        )}
                      </td>
                      <td className={tdBase}>—</td>
                    </tr>
                    {/* Inline Add Form Row */}
                    {openAddFormKey === key && (
                      <tr className="bg-rowActive border-b border-line">
                        <td className={`${tdBase} pl-8`}></td>
                        <td className={tdBase}></td>
                        <td className={tdBase}></td>
                        <td className={tdBase}></td>
                        <td className={tdBase}>
                          <select
                            aria-label="Category"
                            value={addFormCategory}
                            onChange={e => { setAddFormCategory(e.target.value); setAddFormError(''); }}
                            className={fieldClass}
                          >
                            <option value="">Category</option>
                            {activeCategories.map(c => (
                              <option key={c.id} value={c.name}>{c.name}</option>
                            ))}
                          </select>
                          <TaskLabelField
                            value={addFormTaskLabel}
                            onChange={(v) => { setAddFormTaskLabel(v); setAddFormError(''); }}
                            required={Boolean(addFormCategory) && categoryExistsForUser(parent, addFormCategory)}
                            error={addFormError}
                          />
                        </td>
                        {/* Date Created */}
                        <td className={tdBase}>
                          {(() => {
                            const now = new Date();
                            return `${now.getDate().toString().padStart(2, '0')}/${(now.getMonth() + 1).toString().padStart(2, '0')}/${now.getFullYear()}`;
                          })()}
                        </td>
                        {/* Date Modified */}
                        <td className={tdBase}>
                          {(() => {
                            const now = new Date();
                            return `${now.getDate().toString().padStart(2, '0')}/${(now.getMonth() + 1).toString().padStart(2, '0')}/${now.getFullYear()}`;
                          })()}
                        </td>
                        <td className={`${tdBase} text-right tabular`}>0.0</td>
                        <td className={`${tdBase} text-right tabular`}>0.0</td>
                        <td className={tdBase} colSpan={3}>
                          <button
                            className="bg-accent text-white font-semibold rounded-tc-sm px-5 min-h-[44px] mr-2"
                            onClick={async () => {
                              if (!addFormCategory) {
                                toast.error('Category required');
                                return;
                              }
                              // Use clientId from group key
                              if (!clientId) {
                                toast.error('Client not found.');
                                return;
                              }
                              // Same category may repeat, but then a task label is required.
                              if (categoryExistsForUser(parent, addFormCategory) && !addFormTaskLabel.trim()) {
                                setAddFormError('Task name is required because this category already exists on the project.');
                                return;
                              }
                              // Allow multiple users to add the same category to the same project
                              const now = new Date();
                              const todayStr = `${now.getDate().toString().padStart(2, '0')}/${(now.getMonth() + 1).toString().padStart(2, '0')}/${now.getFullYear()}`;
                              try {
                                await axios.post(`${API_BASE_URL}/api/entries`, {
                                  project,
                                  client_id: clientId,
                                  requestedBy,
                                  department,
                                  category: addFormCategory,
                                  category_id: categoryByName[addFormCategory]?.id ?? null,
                                  task_label: addFormTaskLabel.trim(),
                                  hours: 0,
                                  dateCreated: todayStr,
                                  dateModified: todayStr,
                                  user: loggedInUser
                                });
                                setOpenAddFormKey(null);
                                setAddFormCategory('');
                                setAddFormTaskLabel('');
                                setAddFormError('');
                                fetchEntries();
                                toast.success('Entry added');
                              } catch (err) {
                                const message = err.response?.data?.error || 'Failed to add entry';
                                if (err.response?.data?.field) setAddFormError(message); else toast.error(message);
                              }
                            }}
                          >Save</button>
                          <button
                            className="bg-surface text-muted border border-line rounded-tc-sm px-4 min-h-[44px]"
                            onClick={() => setOpenAddFormKey(null)}
                          >Cancel</button>
                        </td>
                        <td className={`${tdBase} text-faint`}>—</td>
                      </tr>
                    )}
                    {/* Child Rows */}
                    {children.map((entry) => (
                      <tr key={entry.id} className={`border-b border-line/60 ${activeTimer === entry.id ? 'bg-rowActive' : 'bg-surface'}`}>
                        <td className={`${tdBase} pl-6`}></td>
                        <td className={tdBase}></td>
                        <td className={tdBase}></td>
                        <td className={tdBase}></td>
                        <td className={tdBase}>
                          {editingId === entry.id ? (
                            <>
                              <select
                                value={editCategory ? editCategory.name : ''}
                                onChange={e => { setEditCategory({ id: entry.id, name: e.target.value }); setEditBrutoOverridden(false); setEditError(''); }}
                                className={`${fieldClass} !min-h-[38px] !py-1.5`}
                                aria-label="Category"
                              >
                                <option value="">Select Category</option>
                                {categories
                                  .filter(c => c.is_active || c.name === getCategoryName(entry.category))
                                  .map(c => (
                                    <option key={c.id} value={c.name}>{c.name}</option>
                                  ))}
                              </select>
                              <TaskLabelField
                                value={editTaskLabel}
                                onChange={(v) => { setEditTaskLabel(v); setEditError(''); }}
                                required={Boolean(editCategory?.name) && emptyLabelCollides(entry, editCategory.name, entry.id)}
                                error={editError}
                              />
                            </>
                          ) : (
                            <CategoryCell entry={entry} />
                          )}
                          <WorkNoteCell entry={entry} isOwner={entry.user === loggedInUser} onSave={handleSaveNote} />
                        </td>
                        <td className={tdBase}>
                          {editingId === entry.id ? (
                            <input
                              type="text"
                              value={editDate}
                              onChange={(e) => { setEditDate(e.target.value); setEditBrutoOverridden(false); }}
                              className={`${fieldClass} !min-h-[38px] !py-1.5`}
                              aria-label="Date created"
                              placeholder="DD/MM/YYYY"
                            />
                          ) : (
                            formatDate(entry.dateCreated || entry.date)
                          )}
                        </td>
                        <td className={tdBase}>
                          {formatDate(entry.dateModified || entry.date)}
                        </td>
                        <td className={`${tdBase} text-right`}>
                          {editingId === entry.id ? (
                            <div className="flex items-center justify-end">
                              {/* Prevent editing if not owner */}
                              {entry.user !== loggedInUser ? (
                                <span className="text-danger-ink">You cannot edit hours for a category you do not own.</span>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => { setEditHours((prev) => Math.max(0, (parseFloat(prev) || 0) - 0.5).toFixed(1)); setEditBrutoOverridden(false); }}
                                    className={`${stepBtn} border border-line rounded-l-tc-sm`}
                                    aria-label="Decrease by 0.5 h"
                                  >−</button>
                                  <input
                                    type="number"
                                    step="0.5"
                                    value={editHours}
                                    onChange={(e) => { setEditHours(e.target.value); setEditBrutoOverridden(false); }}
                                    className="w-20 text-center text-sm font-semibold tabular text-ink bg-surface border-y border-line-strong px-2 py-2 min-h-[44px]"
                                    aria-label="Neto hours"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => { setEditHours((prev) => ((parseFloat(prev) || 0) + 0.5).toFixed(1)); setEditBrutoOverridden(false); }}
                                    className={`${stepBtn} border border-line rounded-r-tc-sm`}
                                    aria-label="Increase by 0.5 h"
                                  >+</button>
                                </>
                              )}
                            </div>
                          ) : (
                            <span className="inline-flex items-center border border-line rounded-tc-sm overflow-hidden">
                              {/* Prevent adding hours if not owner */}
                              <button
                                type="button"
                                className={`${stepBtn} border-r border-line`}
                                aria-label="Decrease by 0.5 h"
                                onClick={async () => {
                                  if (entry.user !== loggedInUser) {
                                    toast.error('You cannot edit hours for a category you do not own.');
                                    return;
                                  }
                                  const newHours = Math.max(0, (parseFloat(entry.hours) || 0) - 0.5);
                                  if (!confirmOverrideLoss(entry, { hours: newHours })) return;
                                  const now = new Date();
                                  const todayStr = `${now.getDate().toString().padStart(2, '0')}/${(now.getMonth() + 1).toString().padStart(2, '0')}/${now.getFullYear()}`;
                                  await axios.put(`${API_BASE_URL}/api/entries/${entry.id}`,
                                    { ...entry, hours: newHours, dateModified: todayStr });
                                  fetchEntries();
                                }}
                                disabled={parseFloat(entry.hours) <= 0 || entry.user !== loggedInUser}
                              >−</button>
                              <span className="min-w-[54px] px-2 text-sm font-semibold tabular text-center">
                                {activeTimer === entry.id
                                  ? (Number(entry.hours) + timerHours).toFixed(1)
                                  : Number(entry.hours).toFixed(1)}
                              </span>
                              <button
                                type="button"
                                className={`${stepBtn} border-l border-line`}
                                aria-label="Increase by 0.5 h"
                                onClick={async () => {
                                  if (entry.user !== loggedInUser) {
                                    toast.error('You cannot edit hours for a category you do not own.');
                                    return;
                                  }
                                  const newHours = (parseFloat(entry.hours) || 0) + 0.5;
                                  if (!confirmOverrideLoss(entry, { hours: newHours })) return;
                                  const now = new Date();
                                  const todayStr = `${now.getDate().toString().padStart(2, '0')}/${(now.getMonth() + 1).toString().padStart(2, '0')}/${now.getFullYear()}`;
                                  await axios.put(`${API_BASE_URL}/api/entries/${entry.id}`,
                                    { ...entry, hours: newHours, dateModified: todayStr });
                                  fetchEntries();
                                }}
                                disabled={entry.user !== loggedInUser}
                              >+</button>
                            </span>
                          )}
                        </td>
                        <td className={`${tdBase} text-right`}>
                          <BrutoCell
                            entry={entry}
                            editing={editingId === entry.id && entry.user === loggedInUser}
                            locked={editingId === entry.id
                              ? !isBrutoRuleActive(normalizeEntryDate(editDate))
                              : !isBrutoRuleActive(resolveEntryDate(entry))}
                            value={editingId === entry.id ? getEditBrutoValue() : entry.hours_bruto}
                            neto={editingId === entry.id ? editHours : entry.hours}
                            overridden={editBrutoOverridden}
                            onChange={(v) => { setEditBruto(v); setEditBrutoOverridden(true); }}
                          />
                        </td>
                        <td className={tdBase}>
                          {activeTimer === entry.id ? (
                            <>
                              <div className="text-sm font-semibold tabular text-accent">{formatElapsed(elapsed)}</div>
                              <div className="text-[10.5px] text-muted tabular">
                                {`${Math.ceil((1800000 - (elapsed % 1800000)) / 60000)} min to +0.5 h`}
                              </div>
                            </>
                          ) : (
                            <span className="text-faint">—</span>
                          )}
                        </td>
                        <td className={tdBase}>
                          {/* Actions column */}
                          {editingId !== entry.id && (
                            <div className="flex items-center gap-1.5">
                              {activeTimer === entry.id ? (
                                <button
                                  onClick={async () => {
                                    // Update dateModified to today
                                    const now = new Date();
                                    const todayStr = `${now.getDate().toString().padStart(2, '0')}/${(now.getMonth() + 1).toString().padStart(2, '0')}/${now.getFullYear()}`;
                                    await axios.put(`${API_BASE_URL}/api/entries/${entry.id}`,
                                      { ...entry, dateModified: todayStr });
                                    stopTimer(entry.id);
                                  }}
                                  className={`${iconBtnBase} bg-accent text-white border-0`}
                                  aria-label="Stop timer"
                                  title="Stop timer"
                                >
                                  <svg {...svgIcon} fill="currentColor" stroke="none"><rect x="6" y="6" width="12" height="12" rx="2.5" /></svg>
                                </button>
                              ) : (
                                <button
                                  onClick={async () => {
                                    // Update dateModified to today
                                    const now = new Date();
                                    const todayStr = `${now.getDate().toString().padStart(2, '0')}/${(now.getMonth() + 1).toString().padStart(2, '0')}/${now.getFullYear()}`;
                                    await axios.put(`${API_BASE_URL}/api/entries/${entry.id}`,
                                      { ...entry, dateModified: todayStr });
                                    startTimer(entry.id);
                                  }}
                                  className={`${iconBtnBase} bg-accent-soft text-accent border border-accent-line`}
                                  aria-label="Start timer"
                                  title="Start timer"
                                >
                                  <svg {...svgIcon} fill="currentColor" stroke="none"><path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.5-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z" /></svg>
                                </button>
                              )}
                              <button
                                onClick={() => handleEdit(entry)}
                                className={`${iconBtnBase} bg-surface text-muted border border-line`}
                                aria-label="Edit entry"
                                title="Edit entry"
                              >
                                <svg {...svgIcon}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>
                              </button>
                              <button
                                onClick={() => handleDelete(entry.id)}
                                className={`${iconBtnBase} bg-surface text-muted border border-line`}
                                aria-label="Delete entry"
                                title="Delete entry"
                              >
                                <svg {...svgIcon}><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 11v6M14 11v6" /></svg>
                              </button>
                            </div>
                          )}
                          {editingId === entry.id && (
                            <button
                              onClick={() => handleSaveEdit(entry.id, entry)}
                              className="bg-accent text-white text-[12.5px] font-semibold rounded-tc-sm px-4 min-h-[44px]"
                            >Save</button>
                          )}
                        </td>
                        <td className={tdBase}>
                          {/* User column now last */}
                          {/* Owner is fixed; the server enforces ownership. */}
                          <span>{entry.user}</span>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>

      <p className="mt-4 text-[11.5px] leading-relaxed text-muted">
        The timer adds 0.5 h for every full 30 minutes. Anything under 30 minutes is kept against that row and continues the next time it runs. Only one timer can run at a time.
      </p>

      <div className="mt-4 text-right font-semibold tabular">
        Total Neto: {totalHours.toFixed(1)} h · Total Bruto: {totalBruto.toFixed(1)} h
      </div>
    </div>
  );
}
